/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSetRecoilState } from "recoil";
import DeleteRecordButton from "../input/DeleteRecordButton";
import BasicCrudScreenType from "../screenTypes/functional/BasicCrudScreenType";
import BasicCrudScreenActionItemLayout from "../screenTypes/layout/BasicCrudScreenActionItemLayout";
import AccessControl from "../functional/AccessControl";
import getSchema from "../../lib/api/getSchema";
import notificationState from "../../states/notificationState";
import CommonCodeSlaveCreateButton from "../input/CommonCodeSlaveCreateButton";
import UpdateRecordButton from "../input/UpdateRecordButton";
/**
 * @COMPONENT_NAME CommonCodeSlave
 * @DESCRIPTION 공통코드 슬레이브 관리를 위한 임베디드 컴포넌트입니다.
 *
 * @PROPS
 *    - {Object} selectedmst - 선택한 마스터 그리드 데이터 Object
 *	  - {String} call - api 호출 시 시간 정보
 * @CALLBACKS
 *    - {String} viewSchemaUrl - 스키마 변경 api url
 *    - {Function} onModifyViewUrl - view 변경 콜백함수
 *	  - {Function} onApiInitialized - API 초기화 시 호출되는 콜백 함수
 *	  - {Function} onFailure - 등록, 수정, 삭제 실패 시 호출되는 콜백 함수
 *	  - {Function} onNotifyWarning - 등록, 수정, 삭제 시 경고 트리거 시 경고 알림 콜백 함수
 *	  - {Function} onSuccess - 등록, 수정, 삭제 시 호출되는 콜백 함수
 */
function CommonCodeSlave(props) {
	const { t } = useTranslation();
	const { selectedmst, call } = props;

	const table = "CommonCodeSlave";
	const view = "VwCommonCodeSlave";
	const viewSchemaUrl = `/api/CommonCodeSlaveSchema/${selectedmst?.mstCodeId}/${call}`;
	const [schema, setSchema] = useState();
	const [grid, setGrid] = useState();
	const [api, setApi] = useState();
	const [selectedSlave, setSlectedSlave] = useState();

	useEffect(() => {
		let mounted = true;

		async function schemaSetting() {
			let schema = await getSchema(table);

			if (mounted) {
				setSchema(schema);
			}
		}

		schemaSetting();
		return () => {
			mounted = false;
		};
	}, []);

	useEffect(() => {
		if (grid) {
			grid.selectionChanged.addHandler((grid, event) => {
				if (grid.selectedItems && grid.selectedItems[0]) {
					setSlectedSlave(grid.selectedItems[0]);
				} else {
					setSlectedSlave();
				}
			});

			grid.itemsSourceChanged.addHandler((grid, event) => {
				if (grid.selectedItems && grid.selectedItems[0]) {
					setSlectedSlave(grid.selectedItems[0]);
				} else {
					setSlectedSlave();
				}
			});
		}
	}, [grid]);

	useEffect(() => {
		if (!schema || !selectedmst) return;
		if (api) api.refresh();

		const headerArray = Object.keys(selectedmst || {}).filter((key) =>
			key.includes("header")
		);

		for (let schemaRow of schema?.columns || []) {
			const schemaName = schemaRow.name;

			// "rel01" ~ "rel15" 에 해당하는 경우만 처리
			const match = schemaName.match(/rel(\d{2})$/);
			if (!match) continue;

			const relNumber = match[1]; // "01", "02", ..., "15"

			// headerXX 목록 중에서 숫자가 일치하는 항목 찾기
			const headerKey = headerArray.find((key) =>
				key.endsWith(relNumber)
			);

			// 해당 headerKey가 있고, 그 값이 있을 때만 변경
			if (headerKey) {
				schemaRow.label = selectedmst[headerKey] ?? "";
			}
		}
	}, [selectedmst, schema]);

	const setNotification = useSetRecoilState(notificationState);

	const onApiInitialized = (api) => {
		setApi(api);
	};

	const onGridInitialized = (grid) => {
		grid.selectionMode = "Row";
		setGrid(grid);
	};

	const notifySuccess = (msg) => {
		setNotification({
			open: true,
			severity: "success",
			msg: msg,
		});
	};

	const onSuccess = (msg) => {
		notifySuccess(msg);
	};

	const notifyFailure = (msg) => {
		setNotification({
			open: true,
			severity: "error",
			msg: msg,
		});
	};

	const onFailure = (reason) => {
		notifyFailure(reason);
	};

	const notifyWarning = (msg) => {
		setNotification({
			open: true,
			severity: "warning",
			msg: msg,
		});
	};

	const onNotifyWarning = (msg) => {
		notifyWarning(msg);
	};

	return (
		<BasicCrudScreenType
			id={table}
			headerTitle={t("term.commonCodeSlave")}
			table={table}
			view={view}
			onModifyViewUrl={(url) => `${url}/${selectedmst?.mstCodeId}`}
			viewSchemaUrl={viewSchemaUrl}
			noCreate
			noEdit
			noDelete
			noExcel
			onInitialized={onGridInitialized}
			onApiInitialized={onApiInitialized}
			onAppendButtonInFrontOfSearch={() => (
				<>
					<BasicCrudScreenActionItemLayout>
						<AccessControl
							accessTarget={schema && schema.name}
							accessMethod="POST"
						>
							<CommonCodeSlaveCreateButton
								id={table}
								schema={schema}
								refresh={api}
								onSuccess={onSuccess}
								disabled={!selectedmst}
								selectedmst={selectedmst}
							/>
						</AccessControl>

						<AccessControl
							accessTarget={schema && schema.name}
							accessMethod={
								schema &&
								(schema.updateWithPatch ? "PATCH" : "PUT")
							}
						>
							<UpdateRecordButton
								id={table}
								schema={schema}
								grid={grid}
								refresh={api}
								onSuccess={onSuccess}
								disabled={!selectedSlave}
							/>
						</AccessControl>

						<AccessControl
							accessTarget={schema && schema.name}
							accessMethod="DELETE"
						>
							<DeleteRecordButton
								id={table}
								schema={schema}
								grid={grid}
								refresh={api}
								disabled={!selectedSlave}
								color="secondary"
								onSuccess={onSuccess}
								onFailure={onFailure}
								onPartialFailure={onNotifyWarning}
							/>
						</AccessControl>
					</BasicCrudScreenActionItemLayout>
				</>
			)}
		/>
	);
}

export default CommonCodeSlave;
